import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addDays, format, startOfDay, endOfDay, startOfWeek, endOfWeek } from "date-fns";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import ScheduleForm from "@/components/ScheduleForm";

const VIEWS = [
  ["day", "Day"],
  ["week", "Week"],
  ["list", "List"],
];

function rangeFor(view, cursor) {
  if (view === "week") {
    return { start: startOfWeek(cursor, { weekStartsOn: 1 }), end: endOfWeek(cursor, { weekStartsOn: 1 }) };
  }
  if (view === "list") {
    const start = startOfDay(new Date());
    return { start, end: endOfDay(addDays(start, 21)) };
  }
  return { start: startOfDay(cursor), end: endOfDay(cursor) };
}

function whenLabel(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return format(date, "EEE MMM d · h:mm a");
}

export default function Schedule() {
  const nav = useNavigate();
  const [view, setView] = useState("week");
  const [cursor, setCursor] = useState(new Date());
  const [data, setData] = useState({ events: [], unscheduled: [] });
  const [pick, setPick] = useState(null);

  const window = useMemo(() => rangeFor(view, cursor), [view, cursor]);

  const load = () => {
    api.get("/schedule", { params: { start: window.start.toISOString(), end: window.end.toISOString() } })
      .then((response) => setData(response.data))
      .catch(() => {});
  };

  useEffect(() => { load(); }, [window.start.getTime(), window.end.getTime()]);

  const shift = (dir) => {
    const days = view === "week" ? 7 : 1;
    setCursor((current) => addDays(current, dir * days));
  };

  const days = view === "week"
    ? Array.from({ length: 7 }, (_, index) => addDays(startOfWeek(cursor, { weekStartsOn: 1 }), index))
    : [];

  const visible = data.events;

  const weekEvents = (day) => data.events.filter((event) => format(new Date(event.start), "yyyy-MM-dd") === format(day, "yyyy-MM-dd"));
  const shown = view === "week" ? weekEvents(cursor) : visible;

  return (
    <div className="min-h-screen" data-testid="schedule-screen">
      <header className="px-5 pt-7 pb-3">
        <p className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-400">On the board</p>
        <h1 className="font-heading text-3xl font-black tracking-tight">Schedule</h1>
      </header>

      <div className="px-4 flex gap-2">
        {VIEWS.map(([id, label]) => (
          <button
            key={id}
            data-testid={`schedule-view-${id}`}
            onClick={() => { setView(id); if (id !== "list") setCursor(new Date()); }}
            className={`flex-1 h-10 rounded-full text-xs font-bold uppercase tracking-wide ${view === id ? "bg-zinc-900 text-white" : "bg-white border border-zinc-200 text-zinc-600"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {view !== "list" && (
        <div className="px-4 mt-3 flex items-center justify-between">
          <button data-testid="schedule-prev" onClick={() => shift(-1)} className="h-10 w-10 rounded-full border border-zinc-200 bg-white flex items-center justify-center"><CaretLeft size={18} /></button>
          <p className="font-heading font-bold text-zinc-900">
            {view === "week" ? `${format(window.start, "MMM d")} – ${format(window.end, "MMM d")}` : format(cursor, "EEEE, MMM d")}
          </p>
          <button data-testid="schedule-next" onClick={() => shift(1)} className="h-10 w-10 rounded-full border border-zinc-200 bg-white flex items-center justify-center"><CaretRight size={18} /></button>
        </div>
      )}

      {view === "week" && (
        <div className="px-4 mt-3 grid grid-cols-7 gap-1">
          {days.map((day) => {
            const count = weekEvents(day).length;
            const selected = format(day, "yyyy-MM-dd") === format(cursor, "yyyy-MM-dd");
            return (
              <button
                key={day.toISOString()}
                data-testid={`schedule-day-${format(day, "yyyy-MM-dd")}`}
                onClick={() => setCursor(day)}
                className={`rounded-xl py-2 text-center border ${selected ? "bg-orange-600 text-white border-orange-600" : "bg-white border-zinc-200 text-zinc-700"}`}
              >
                <p className="text-[10px] font-bold uppercase">{format(day, "EEE")}</p>
                <p className="font-heading font-black text-lg leading-none">{format(day, "d")}</p>
                <p className={`text-[10px] font-bold ${selected ? "text-orange-100" : "text-zinc-400"}`}>{count || "·"}</p>
              </button>
            );
          })}
        </div>
      )}

      <div className="px-4 mt-4 space-y-3 pb-6">
        {data.unscheduled?.length > 0 && (
          <section data-testid="unscheduled-won">
            <p className="text-[11px] uppercase font-bold text-zinc-400 mb-2">Won, not scheduled</p>
            <div className="space-y-2">
              {data.unscheduled.map((job) => (
                <button
                  key={job.job_id}
                  data-testid={`unscheduled-${job.job_id}`}
                  onClick={() => setPick(job)}
                  className="w-full text-left bg-green-50 border border-green-200 rounded-xl p-3"
                >
                  <p className="font-semibold text-zinc-900">{job.customer_name || "Client"}</p>
                  <p className="text-xs text-zinc-500">{job.location || "No address yet"}</p>
                  <p className="text-xs font-bold text-green-700 mt-1">Schedule this job</p>
                </button>
              ))}
            </div>
          </section>
        )}

        <section className="space-y-2" data-testid="schedule-events">
          {shown.length === 0 && (
            <div className="text-sm text-zinc-400 py-8 text-center border border-dashed border-zinc-200 rounded-xl">
              Nothing on the board {view === "list" ? "for the next three weeks" : "here"}.
            </div>
          )}
          {shown.map((event) => (
            <button
              key={event.job_id + event.start}
              data-testid={`event-${event.job_id}`}
              onClick={() => nav(`/job/${event.job_id}?schedule=1`)}
              className="w-full text-left bg-white border border-zinc-200 rounded-xl p-3.5"
            >
              <p className="text-[11px] font-bold uppercase text-orange-600">{whenLabel(event.start)}</p>
              <p className="font-semibold text-zinc-900">{event.customer_name || "Job"}</p>
              <p className="text-xs text-zinc-500 truncate">{[event.location, event.crew && `Crew ${event.crew}`, event.duration_minutes && `${event.duration_minutes} min`].filter(Boolean).join(" · ")}</p>
            </button>
          ))}
        </section>
      </div>

      <Drawer open={!!pick} onOpenChange={(open) => { if (!open) setPick(null); }}>
        <DrawerContent className="max-w-md mx-auto">
          <DrawerHeader>
            <DrawerTitle className="font-heading">Schedule {pick?.customer_name || "this job"}</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-6">
            {pick && <ScheduleForm jobId={pick.job_id} onDone={() => { setPick(null); load(); }} />}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
